// Administration Page for AAR Guest Management System
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc } from 'firebase/firestore';
import { updateDoc, httpsCallable } from '../firebase/guardedWrites';
import { readOnlyProps } from '../utils/viewAs';
import { db } from '../firebase/config';
import { getCurrentUser, getUserRole, startViewAs } from '../firebase/authService';
import { canAccessAdmin, getAllRoles, getRoleName, getRoleColor } from '../utils/roles';
import { functions } from '../firebase/config';
import '../styles/Administration.css';

const DEFAULT_NEW_USER_ROLE = 'aar_volunteer';

const Administration = () => {
  const ro = readOnlyProps(); // disable write controls while viewing as someone
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('users');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showM365Modal, setShowM365Modal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Create user form state
  const [createForm, setCreateForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'aar_volunteer',
    password: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [showPassword, setShowPassword] = useState(false);

  // Edit user state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ email: '', firstName: '', lastName: '' });

  // M365 search/add state
  const [m365Query, setM365Query] = useState('');
  const [m365Searching, setM365Searching] = useState(false);
  const [m365Results, setM365Results] = useState([]);
  const [m365Selected, setM365Selected] = useState(null);
  const [m365Role, setM365Role] = useState(DEFAULT_NEW_USER_ROLE);
  const m365SearchTimer = useRef(null);

  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const roles = getAllRoles();

  useEffect(() => {
    checkAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAccess = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const role = await getUserRole(currentUser.uid);
    if (!canAccessAdmin(role)) {
      navigate('/dashboard');
      return;
    }

    loadData();
  };

  const loadData = async () => {
    try {
      setLoading(true);
      await loadUsers();
    } catch (error) {
      console.error('Error loading data:', error);
      showMessage('error', 'Failed to load data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const [usersSnapshot, rolesSnapshot, allowedSnapshot] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'userRoles')),
        getDocs(collection(db, 'allowedUsers'))
      ]);

      const rolesMap = {};
      rolesSnapshot.forEach(d => { rolesMap[d.id] = d.data(); });

      const usersList = usersSnapshot.docs.map(d => {
        const userData = d.data();
        const roleData = rolesMap[d.id] || {};
        return {
          uid: d.id,
          ...userData,
          role: roleData.role || 'aar_volunteer',
          disabled: userData.disabled || false,
          pending: false
        };
      });

      // Merge in allowedUsers entries that haven't signed in yet (no matching email in users)
      const existingEmails = new Set(usersList.map(u => (u.email || '').toLowerCase()));
      const pendingList = allowedSnapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(au => !existingEmails.has((au.email || au.id || '').toLowerCase()))
        .map(au => ({
          uid: `allowed:${au.id}`,
          email: au.email || au.id,
          displayName: au.displayName || (au.email || au.id || '').split('@')[0],
          firstName: '',
          lastName: '',
          role: au.role || 'aar_volunteer',
          createdAt: au.addedAt || null,
          lastLogin: null,
          disabled: false,
          pending: true,
          allowedDocId: au.id
        }));

      const combined = [...usersList, ...pendingList];

      // Sort by creation date (newest first)
      combined.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : (a.createdAt || new Date(0));
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : (b.createdAt || new Date(0));
        return dateB - dateA;
      });

      setUsers(combined);
    } catch (error) {
      console.error('Error loading users:', error);
      throw error;
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreateForm({ email: '', firstName: '', lastName: '', role: 'aar_volunteer', password: '', confirmPassword: '' });
    setPasswordErrors([]);
    setShowPassword(false);
  };

  const closeM365Modal = () => {
    if (m365SearchTimer.current) clearTimeout(m365SearchTimer.current);
    setShowM365Modal(false);
    setM365Query('');
    setM365Results([]);
    setM365Selected(null);
    setM365Role(DEFAULT_NEW_USER_ROLE);
    setM365Searching(false);
  };

  const handleM365QueryChange = (value) => {
    setM365Query(value);
    setM365Selected(null);

    if (m365SearchTimer.current) clearTimeout(m365SearchTimer.current);

    if (!value || value.trim().length < 2) {
      setM365Results([]);
      setM365Searching(false);
      return;
    }

    setM365Searching(true);
    m365SearchTimer.current = setTimeout(async () => {
      try {
        const searchM365Users = httpsCallable(functions, 'searchM365Users');
        const result = await searchM365Users({ query: value.trim() });
        setM365Results(result.data?.users || []);
      } catch (error) {
        console.error('M365 search error:', error);
        showMessage('error', error.message || 'Failed to search Microsoft 365.');
        setM365Results([]);
      } finally {
        setM365Searching(false);
      }
    }, 400);
  };

  const handleAddM365User = async () => {
    if (!m365Selected) return;

    try {
      setSubmitting(true);
      const manageUser = httpsCallable(functions, 'manageUser');
      const result = await manageUser({
        action: 'addM365User',
        email: m365Selected.email,
        displayName: m365Selected.displayName,
        azureUserId: m365Selected.azureUserId,
        role: m365Role
      });

      if (result.data?.success) {
        showMessage('success', result.data.message || `${m365Selected.displayName} added.`);
        closeM365Modal();
        loadUsers();
      } else {
        showMessage('error', result.data?.message || 'Failed to add user.');
      }
    } catch (error) {
      console.error('Add M365 user error:', error);
      showMessage('error', error.message || 'Failed to add user.');
    } finally {
      setSubmitting(false);
    }
  };

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) {
      errors.push('At least 8 characters');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('At least one uppercase letter');
    }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(password)) {
      errors.push('At least one special character');
    }
    return errors;
  };

  const handlePasswordChange = (password) => {
    setCreateForm(prev => ({ ...prev, password }));
    setPasswordErrors(validatePassword(password));
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    if (!createForm.email || !createForm.firstName || !createForm.lastName) {
      showMessage('error', 'Please fill in all required fields.');
      return;
    }

    const errors = validatePassword(createForm.password);
    if (errors.length > 0) {
      setPasswordErrors(errors);
      showMessage('error', 'Password does not meet requirements.');
      return;
    }

    if (createForm.password !== createForm.confirmPassword) {
      showMessage('error', 'Passwords do not match.');
      return;
    }

    try {
      setSubmitting(true);

      const manageUser = httpsCallable(functions, 'manageUser');
      const result = await manageUser({
        action: 'create',
        email: createForm.email.toLowerCase(),
        firstName: createForm.firstName,
        lastName: createForm.lastName,
        role: createForm.role,
        password: createForm.password
      });

      if (result.data.success) {
        showMessage('success', `User ${createForm.email} created successfully!`);
        closeCreateModal();
        loadUsers();
      } else {
        showMessage('error', result.data.message || 'Failed to create user.');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      showMessage('error', error.message || 'Failed to create user.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangeRole = async (user, newRole) => {
    // Protect main system administrator
    if (user.email === 'rsalomon@brooklyntab.org') {
      showMessage('error', 'Cannot change role for main system administrator.');
      return;
    }

    try {
      if (user.pending) {
        // Pending M365 user — update via Cloud Function (writes /allowedUsers, mirrors /userRoles if applicable)
        const manageUser = httpsCallable(functions, 'manageUser');
        const result = await manageUser({
          action: 'setAllowedUserRole',
          email: user.email,
          role: newRole
        });
        if (!result.data?.success) {
          showMessage('error', result.data?.message || 'Failed to update role.');
          return;
        }
      } else {
        const roleRef = doc(db, 'userRoles', user.uid);
        await updateDoc(roleRef, {
          role: newRole,
          lastUpdated: new Date(),
          lastUpdatedBy: currentUser.email
        });
      }

      showMessage('success', `Role updated for ${user.email}.`);
      loadUsers();
    } catch (error) {
      console.error('Error updating role:', error);
      showMessage('error', error.message || 'Failed to update role.');
    }
  };

  const openEditModal = (user) => {
    // For pending users we may not have first/last — derive from displayName if needed
    let firstName = user.firstName || '';
    let lastName = user.lastName || '';
    if ((!firstName || !lastName) && user.displayName) {
      const parts = user.displayName.trim().split(/\s+/);
      if (!firstName) firstName = parts[0] || '';
      if (!lastName) lastName = parts.slice(1).join(' ') || '';
    }
    setEditTarget(user);
    setEditForm({
      email: user.email || '',
      firstName,
      lastName
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditTarget(null);
    setEditForm({ email: '', firstName: '', lastName: '' });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editTarget) return;

    const trimmedEmail = (editForm.email || '').trim();
    const trimmedFirst = (editForm.firstName || '').trim();
    const trimmedLast = (editForm.lastName || '').trim();

    if (!trimmedEmail || !trimmedFirst || !trimmedLast) {
      showMessage('error', 'Email, first name, and last name are all required.');
      return;
    }
    if (!trimmedEmail.includes('@')) {
      showMessage('error', 'Please enter a valid email address.');
      return;
    }
    if (
      editTarget.email === 'rsalomon@brooklyntab.org' &&
      trimmedEmail.toLowerCase() !== 'rsalomon@brooklyntab.org'
    ) {
      showMessage('error', 'Cannot change the email of the main system administrator.');
      return;
    }

    try {
      setSubmitting(true);
      const manageUser = httpsCallable(functions, 'manageUser');
      const payload = editTarget.pending
        ? {
            action: 'updateAllowedUser',
            allowedDocId: editTarget.allowedDocId,
            email: trimmedEmail,
            firstName: trimmedFirst,
            lastName: trimmedLast
          }
        : {
            action: 'updateUser',
            userId: editTarget.uid,
            email: trimmedEmail,
            firstName: trimmedFirst,
            lastName: trimmedLast
          };

      const result = await manageUser(payload);
      if (result.data?.success) {
        showMessage('success', result.data.message || 'User updated successfully.');
        closeEditModal();
        loadUsers();
      } else {
        showMessage('error', result.data?.message || 'Failed to update user.');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      showMessage('error', error.message || 'Failed to update user.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisableUser = async (user) => {
    if (!window.confirm(`${user.disabled ? 'Enable' : 'Disable'} ${user.email}?`)) {
      return;
    }

    try {
      const manageUser = httpsCallable(functions, 'manageUser');
      const result = await manageUser({
        action: user.disabled ? 'enable' : 'disable',
        userId: user.uid
      });

      if (result.data.success) {
        showMessage('success', `User ${user.disabled ? 'enabled' : 'disabled'} successfully.`);
        loadUsers();
      } else {
        showMessage('error', result.data.message || `Failed to ${user.disabled ? 'enable' : 'disable'} user.`);
      }
    } catch (error) {
      console.error('Error toggling user:', error);
      showMessage('error', `Failed to ${user.disabled ? 'enable' : 'disable'} user.`);
    }
  };

  // See the app exactly as this member does, read-only. The header banner
  // offers the way back.
  const handleViewAs = async (user) => {
    try {
      await startViewAs({
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim()
      });
    } catch (err) {
      console.error('Error starting view as:', err);
      showMessage('error', err.message || 'Could not start viewing as this user.');
    }
  };

  const handleDeleteUser = async (user) => {
    if (user.pending) {
      // Pending entry — just remove from /allowedUsers, no Auth account exists yet
      if (!window.confirm(`Remove pending user ${user.email}? They will not be able to sign in.`)) {
        return;
      }
      try {
        const manageUser = httpsCallable(functions, 'manageUser');
        const result = await manageUser({ action: 'removeAllowedUser', email: user.email });
        if (result.data?.success) {
          showMessage('success', `Pending user ${user.email} removed.`);
          loadUsers();
        } else {
          showMessage('error', result.data?.message || 'Failed to remove user.');
        }
      } catch (error) {
        console.error('Error removing pending user:', error);
        showMessage('error', error.message || 'Failed to remove pending user.');
      }
      return;
    }

    const confirmText = window.prompt(
      `WARNING: This will permanently delete ${user.email} and all associated data.\n\nType "DELETE" to confirm:`
    );

    if (confirmText !== 'DELETE') {
      return;
    }

    try {
      const manageUser = httpsCallable(functions, 'manageUser');
      const result = await manageUser({
        action: 'delete',
        userId: user.uid
      });

      if (result.data.success) {
        showMessage('success', `User ${user.email} deleted successfully.`);
        loadUsers();
      } else {
        showMessage('error', result.data.message || 'Failed to delete user.');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      showMessage('error', 'Failed to delete user.');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="loading"><div className="spinner"></div><p>Loading administration panel...</p></div>
    );
  }

  return (
    <div className="administration-container">
      <div className="admin-header-nav">
        <button className="btn-back" onClick={() => navigate('/admin')}>
          ← Back to Admin Panel
        </button>
        <h1 className="admin-title">User Administration</h1>
      </div>

      {message.text && (
        <div className={`message ${message.type === 'error' ? 'error-message' : 'success-message'}`}>
          {message.text}
        </div>
      )}

      <div className="admin-tabs-container">
        <div className="admin-tabs">
          <button
            className={`tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            Users ({users.length})
          </button>
          <button
            className={`tab ${activeTab === 'roles' ? 'active' : ''}`}
            onClick={() => setActiveTab('roles')}
          >
            Roles & Permissions
          </button>
        </div>
        <div className="admin-actions">
          <button className="btn btn-invite" onClick={() => setShowM365Modal(true)} disabled={ro.disabled} title={ro.title}>
            + Add M365 User
          </button>
          <button className="btn btn-outline" onClick={() => setShowCreateModal(true)} disabled={ro.disabled} title={ro.title}>
            + Create User (Email)
          </button>
        </div>
      </div>

      <div className="admin-content">
        {activeTab === 'users' && (
          <div className="users-section">
            {users.length === 0 ? (
              <div className="empty-state">
                <p>No users found.</p>
              </div>
            ) : (
              <div className="users-table-container">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Last Login</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(user => (
                      <tr key={user.uid} className={`${user.disabled ? 'disabled-row' : ''} ${user.pending ? 'pending-row' : ''}`}>
                        <td>
                          <div className="user-name">
                            {user.displayName || `${user.firstName} ${user.lastName}`}
                          </div>
                        </td>
                        <td>{user.email}</td>
                        <td>
                          <select
                            value={user.role}
                            onChange={(e) => handleChangeRole(user, e.target.value)}
                            className="role-select"
                            style={{ borderColor: getRoleColor(user.role) }}
                            disabled={user.email === 'rsalomon@brooklyntab.org' || ro.disabled}
                            title={ro.title || (user.email === 'rsalomon@brooklyntab.org' ? 'Cannot change role for main system administrator' : '')}
                          >
                            {roles.map(role => (
                              <option key={role.id} value={role.id}>
                                {role.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          {user.pending ? (
                            <span className="status-badge pending" title="Added from M365 — awaiting first sign-in">
                              Pending sign-in
                            </span>
                          ) : (
                            <span className={`status-badge ${user.disabled ? 'disabled' : 'active'}`}>
                              {user.disabled ? 'Disabled' : 'Active'}
                            </span>
                          )}
                        </td>
                        <td className="date-cell">{formatDate(user.createdAt)}</td>
                        <td className="date-cell">{user.pending ? 'Never' : formatDate(user.lastLogin)}</td>
                        <td>
                          <div className="action-buttons">
                            {!user.pending && user.uid !== currentUser?.uid && (
                              <button
                                className="btn-icon"
                                onClick={() => handleViewAs(user)}
                                disabled={ro.disabled}
                                title={ro.disabled ? 'Exit the current "View as" first' : `View the app as ${user.displayName || user.email} (read-only)`}
                              >
                                👁️
                              </button>
                            )}
                            <button
                              className="btn-icon"
                              onClick={() => openEditModal(user)}
                              disabled={ro.disabled}
                              title={ro.title || 'Edit user info'}
                            >
                              ✏️
                            </button>
                            {!user.pending && (
                              <button
                                className="btn-icon"
                                onClick={() => handleDisableUser(user)}
                                title={ro.title || (user.email === 'rsalomon@brooklyntab.org' ? 'Cannot disable main system administrator' : (user.disabled ? 'Enable user' : 'Disable user'))}
                                disabled={user.email === 'rsalomon@brooklyntab.org' || ro.disabled}
                              >
                                {user.disabled ? '✓' : '🚫'}
                              </button>
                            )}
                            <button
                              className="btn-icon delete"
                              onClick={() => handleDeleteUser(user)}
                              title={ro.title || (user.email === 'rsalomon@brooklyntab.org' ? 'Cannot delete main system administrator' : (user.pending ? 'Remove pending user' : 'Delete user'))}
                              disabled={user.email === 'rsalomon@brooklyntab.org' || ro.disabled}
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'roles' && (
          <div className="roles-section">
            <h2>Role Permissions Matrix</h2>
            <div className="roles-grid">
              {roles.map(role => (
                <div key={role.id} className="role-card" style={{ borderColor: role.color }}>
                  <div className="role-header" style={{ backgroundColor: role.color }}>
                    <h3>{role.name}</h3>
                  </div>
                  <div className="role-body">
                    <p className="role-description">{role.description}</p>
                    <div className="role-permissions">
                      <strong>Permissions:</strong>
                      <ul>
                        {role.permissions.includes('all') ? (
                          <li>✓ All permissions</li>
                        ) : (
                          role.permissions.map(perm => (
                            <li key={perm}>✓ {perm.replace(':', ' ')}</li>
                          ))
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={closeCreateModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New User</h2>
              <button className="modal-close" onClick={closeCreateModal}>
                ×
              </button>
            </div>
            <form onSubmit={handleCreateUser} className="invite-form">
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={createForm.firstName}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, firstName: e.target.value }))}
                    required
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    value={createForm.lastName}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, lastName: e.target.value }))}
                    required
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
                  required
                  disabled={submitting}
                  autoComplete="username"
                />
              </div>

              <div className="form-group">
                <label>Password *</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={createForm.password}
                    onChange={(e) => handlePasswordChange(e.target.value)}
                    required
                    disabled={submitting}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Confirm Password *</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={createForm.confirmPassword}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    required
                    disabled={submitting}
                    autoComplete="new-password"
                  />
                </div>
                {createForm.confirmPassword && createForm.password !== createForm.confirmPassword && (
                  <div className="password-mismatch">Passwords do not match</div>
                )}
              </div>

              <div className="password-requirements">
                <span className={createForm.password.length >= 8 ? 'met' : ''}>
                  8+ characters
                </span>
                <span className={/[A-Z]/.test(createForm.password) ? 'met' : ''}>
                  Uppercase letter
                </span>
                <span className={/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(createForm.password) ? 'met' : ''}>
                  Special character
                </span>
                <span className="password-reset-note">
                  Users can reset their own password via email reset link
                </span>
              </div>

              <div className="form-group">
                <label>Role *</label>
                <select
                  value={createForm.role}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, role: e.target.value }))}
                  required
                  disabled={submitting}
                >
                  {roles.map(role => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={closeCreateModal}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting || passwordErrors.length > 0 || createForm.password !== createForm.confirmPassword || !createForm.password || ro.disabled}
                >
                  {submitting ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add M365 User Modal */}
      {showM365Modal && (
        <div className="modal-overlay" onClick={() => !submitting && closeM365Modal()}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add User from Microsoft 365</h2>
              <button className="modal-close" onClick={closeM365Modal} disabled={submitting}>
                ×
              </button>
            </div>
            <div className="invite-form">
              <div className="form-group">
                <label>Search by name or email</label>
                <input
                  type="text"
                  value={m365Query}
                  onChange={(e) => handleM365QueryChange(e.target.value)}
                  placeholder="Start typing a name or email…"
                  autoFocus
                  disabled={submitting}
                />
                {m365Searching && <div className="search-hint">Searching Microsoft 365…</div>}
              </div>

              {m365Results.length > 0 && (
                <div className="m365-results">
                  {m365Results.map(u => {
                    const alreadyAdded = users.some(
                      x => (x.email || '').toLowerCase() === (u.email || '').toLowerCase()
                    );
                    const isSelected = m365Selected?.azureUserId === u.azureUserId;
                    return (
                      <div
                        key={u.azureUserId}
                        className={`m365-result ${isSelected ? 'selected' : ''} ${alreadyAdded ? 'already-added' : ''}`}
                        onClick={() => !alreadyAdded && setM365Selected(u)}
                      >
                        <div className="m365-result-info">
                          <strong>{u.displayName}</strong>
                          <span className="m365-result-email">{u.email}</span>
                          {u.jobTitle && <span className="m365-result-title">{u.jobTitle}</span>}
                        </div>
                        {alreadyAdded && <span className="status-badge disabled">Already added</span>}
                      </div>
                    );
                  })}
                </div>
              )}

              {!m365Searching && m365Query.trim().length >= 2 && m365Results.length === 0 && (
                <div className="search-hint">No matching users found.</div>
              )}

              {m365Selected && (
                <div className="m365-selected">
                  <div className="form-group">
                    <label>Selected user</label>
                    <div className="m365-selected-info">
                      <strong>{m365Selected.displayName}</strong>
                      <span>{m365Selected.email}</span>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Assign Role *</label>
                    <select
                      value={m365Role}
                      onChange={(e) => setM365Role(e.target.value)}
                      disabled={submitting}
                    >
                      {roles.map(role => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={closeM365Modal}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAddM365User}
                  disabled={submitting || !m365Selected || ro.disabled}
                >
                  {submitting ? 'Adding…' : 'Add User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editTarget && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit User</h2>
              <button className="modal-close" onClick={closeEditModal}>×</button>
            </div>
            <form onSubmit={handleSaveEdit} className="invite-form">
              {editTarget.pending && (
                <div className="message" style={{ marginBottom: '12px', background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>
                  This is a pending M365 user (no Auth account yet). Edits update the allowlist entry.
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={editForm.firstName}
                    onChange={(e) => setEditForm(prev => ({ ...prev, firstName: e.target.value }))}
                    required
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    value={editForm.lastName}
                    onChange={(e) => setEditForm(prev => ({ ...prev, lastName: e.target.value }))}
                    required
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                  required
                  disabled={submitting || editTarget.email === 'rsalomon@brooklyntab.org'}
                  autoComplete="off"
                />
                {editTarget.email === 'rsalomon@brooklyntab.org' && (
                  <small style={{ color: '#6b7280' }}>
                    The main system administrator's email cannot be changed.
                  </small>
                )}
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-outline" onClick={closeEditModal} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting || ro.disabled}>
                  {submitting ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Administration;
